import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Save, Loader2 } from 'lucide-react';
import { TextInput, TextArea, MultiSelectChips } from '../FormElements';
import { FileUpload } from './FileUpload';
interface ProfileEditorProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  role: 'brand' | 'organizer';
  initialData: any;
}
export function ProfileEditor({
  isOpen,
  onClose,
  onSave,
  role,
  initialData
}: ProfileEditorProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    tagline: initialData?.tagline || '',
    description: initialData?.description || '',
    origin: initialData?.origin || initialData?.mission || '',
    website: initialData?.website || '',
    instagram: initialData?.instagram || '',
    linkedin: initialData?.linkedin || '',
    interests: initialData?.interests || [],
    values: initialData?.values || [],
    primaryGoal: initialData?.primaryGoal || '',
    dreamMatch: initialData?.dreamMatch || initialData?.idealSponsor || ''
  });
  const accentColor = role === 'brand' ? 'cyan' : 'purple';
  const buttonColor = role === 'brand' ? 'bg-cyan-500 hover:bg-cyan-400 text-slate-900' : 'bg-purple-600 hover:bg-purple-500 text-white';
  const handleSave = async () => {
    setIsSaving(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    onSave(formData);
    setIsSaving(false);
    onClose();
  };
  const interestOptions = ['Sports', 'Music', 'Tech', 'Gaming', 'Lifestyle', 'Business', 'Art', 'Food', 'Fitness', 'Travel', 'Fashion', 'Sustainability'];
  const valueOptions = ['Sustainability', 'Innovation', 'Diversity', 'Health', 'Education', 'Entertainment', 'Social Impact'];
  return <AnimatePresence data-id="element-458">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose} data-id="element-459">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} onClick={e => e.stopPropagation()} className="w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl my-8" data-id="element-460">
            {/* Header */}
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 p-6 flex items-center justify-between rounded-t-2xl z-10" data-id="element-461">
              <div data-id="element-462">
                <h2 className="text-xl font-bold text-white" data-id="element-463">Edit Profile</h2>
                <p className="text-sm text-slate-400" data-id="element-464">
                  Update your profile information
                </p>
              </div>
              <button onClick={onClose} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors" data-id="element-465">
                <X size={20} data-id="element-466" />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-6" data-id="element-467">
              <div data-id="element-468">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-469">
                  Basic Info
                </h3>
                <TextInput label={role === 'brand' ? 'Company Name' : 'Event Name'} value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} data-id="element-470" />
                <TextInput label="Tagline" placeholder="A catchy one-liner" value={formData.tagline} onChange={e => setFormData({
              ...formData,
              tagline: e.target.value
            })} data-id="element-471" />
                <TextArea label={role === 'brand' ? 'What do you do?' : 'About the event'} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} data-id="element-472" />
                <TextArea label={role === 'brand' ? 'Your origin story' : 'Mission'} value={formData.origin} onChange={e => setFormData({
              ...formData,
              origin: e.target.value
            })} data-id="element-473" />
              </div>

              <div data-id="element-474">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-475">
                  Media
                </h3>
                <FileUpload label="Logo / Main Image" accentColor={accentColor} data-id="element-476" />
                <FileUpload label="Gallery Images" accentColor={accentColor} data-id="element-477" />
              </div>

              <div data-id="element-478">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-479">
                  Links
                </h3>
                <TextInput label="Website" placeholder="https://" value={formData.website} onChange={e => setFormData({
              ...formData,
              website: e.target.value
            })} data-id="element-480" />
                <div className="grid grid-cols-2 gap-4" data-id="element-481">
                  <TextInput label="Instagram" placeholder="@handle" value={formData.instagram} onChange={e => setFormData({
                ...formData,
                instagram: e.target.value
              })} data-id="element-482" />
                  <TextInput label="LinkedIn" placeholder="URL" value={formData.linkedin} onChange={e => setFormData({
                ...formData,
                linkedin: e.target.value
              })} data-id="element-483" />
                </div>
              </div>

              <div data-id="element-484">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-485">
                  Interests & Values
                </h3>
                <MultiSelectChips label="Interests" options={interestOptions} selectedValues={formData.interests} onChange={vals => setFormData({
              ...formData,
              interests: vals
            })} accentColor={accentColor} data-id="element-486" />
                <MultiSelectChips label="Values" options={valueOptions} selectedValues={formData.values} onChange={vals => setFormData({
              ...formData,
              values: vals
            })} accentColor={accentColor} data-id="element-487" />
              </div>

              <div data-id="element-488">
                <h3 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4" data-id="element-489">
                  Goals
                </h3>
                <TextInput label="Primary Goal" value={formData.primaryGoal} onChange={e => setFormData({
              ...formData,
              primaryGoal: e.target.value
            })} data-id="element-490" />
                <TextArea label={role === 'brand' ? 'Dream partner description' : 'Ideal sponsor profile'} value={formData.dreamMatch} onChange={e => setFormData({
              ...formData,
              dreamMatch: e.target.value
            })} data-id="element-491" />
              </div>
            </div>

            {/* Footer */}
            <div className="sticky bottom-0 bg-slate-900 border-t border-slate-800 p-6 flex justify-end gap-4 rounded-b-2xl" data-id="element-492">
              <button onClick={onClose} className="px-6 py-3 rounded-full font-medium text-slate-400 hover:text-white hover:bg-slate-800 transition-colors" data-id="element-493">
                Cancel
              </button>
              <button onClick={handleSave} disabled={isSaving} className={`px-8 py-3 rounded-full font-bold transition-all flex items-center gap-2 ${buttonColor}`} data-id="element-494">
                {isSaving ? <>
                    <Loader2 size={18} className="animate-spin" data-id="element-495" /> Saving...
                  </> : <>
                    <Save size={18} data-id="element-496" /> Save Changes
                  </>}
              </button>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}